type WeekLabel = Readonly<{ name: string }>;

/** Pinned column widths shared by the populated matrix and its skeleton. */
export const annualScheduleColumnWidths = {
  subject: 200,
  teacher: 200,
  component: 112,
} as const;

const pinnedWidth = Object.values(annualScheduleColumnWidths).reduce(
  (sum, width) => sum + width,
  0,
);
const tableBorderWidth = 2;
const minimumWeekWidth = 40;
const estimatedCharacterWidth = 10;
const weekCellHorizontalPadding = 16;

/**
 * Gives each week a readable minimum width, then shares unused space evenly.
 *
 * @param weeks - Visible week labels, in display order.
 * @param containerWidth - Measured width of the matrix container in pixels.
 * @returns Pixel widths for MatrixTable; narrow containers retain horizontal scrolling.
 *
 * @remarks The label measurement is an estimate rather than DOM text measurement.
 * The two-pixel table border is reserved before distributing whole leftover pixels,
 * so rounding does not introduce an unwanted scrollbar on wide layouts.
 */
export const getAnnualWeekWidths = (
  weeks: readonly WeekLabel[],
  containerWidth: number,
): number[] => {
  if (!weeks.length) return [];

  const minimums = weeks.map(week =>
    Math.max(
      minimumWeekWidth,
      (week.name?.length ?? 0) * estimatedCharacterWidth + weekCellHorizontalPadding,
    ),
  );
  const minimumTotal = minimums.reduce((sum, width) => sum + width, 0);
  const freeSpace = Math.max(
    0,
    Math.floor(containerWidth) - tableBorderWidth - pinnedWidth - minimumTotal,
  );
  const extraPerWeek = Math.floor(freeSpace / weeks.length);
  const remainder = freeSpace % weeks.length;

  return minimums.map((minimum, index) => minimum + extraPerWeek + (index < remainder ? 1 : 0));
};
